import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

import PairTokenLogos from '../../components/PairTokenLogos'
import { useTokenPrices } from '../../data/stats'
import {
  FARM_ADDRESSES,
  formatUsd,
  getExplorerUrl,
  LPHolder,
  PairData,
  useSunsetData
} from '../../data/sunset'
import { useActiveWeb3React } from '../../hooks'
import {
  FarmPosition,
  formatStakedBalance,
  useFarmPosition,
  useFarmUnstake
} from '../../hooks/useFarmUnstake'
import { useLivePositions } from '../../hooks/useLivePositions'
import { useLPBalance, useSunsetWithdraw } from '../../hooks/useSunsetWithdraw'
import { useWalletModalToggle } from '../../state/application/hooks'

const PageWrapper = styled.div`
  width: 100%;
  max-width: 600px;
  padding: 20px;
  margin: 0 auto;
`

const Title = styled.h1`
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 12px;
  color: ${({ theme }) => theme.text1};
  text-align: center;
`

const Subtitle = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.text2};
  margin-bottom: 32px;
  text-align: center;
  line-height: 1.5;
`

const Card = styled.div`
  background: ${({ theme }) => theme.bg2};
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 16px;
`

const PositionItem = styled.div`
  padding: 16px;
  background: ${({ theme }) => theme.bg1};
  border-radius: 12px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`

const PositionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`

const PositionLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const PositionRight = styled.div`
  text-align: right;
`

const ChainBadge = styled.span<{ chain: string }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${({ chain }) => {
    switch (chain) {
      case 'polygon':
        return '#8247e5'
      case 'bnb':
        return '#f0b90b'
      case 'arbitrum':
        return '#28a0f0'
      default:
        return '#666'
    }
  }};
  color: ${({ chain }) => (chain === 'bnb' ? '#000' : '#fff')};
`

const PairName = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.text1};
`

const Value = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.text1};
`

const LPAmount = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.text3};
  margin-top: 2px;
`

const WithdrawButton = styled.button<{ disabled?: boolean }>`
  flex: 1;
  padding: 12px 16px;
  background: ${({ theme, disabled }) => (disabled ? theme.bg3 : theme.primary1)};
  color: ${({ disabled }) => (disabled ? '#999' : 'white')};
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: opacity 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`

const StatusText = styled.div<{ status: 'success' | 'error' | 'pending' }>`
  margin-top: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  background: ${({ status }) =>
    status === 'success'
      ? 'rgba(39, 174, 96, 0.1)'
      : status === 'error'
      ? 'rgba(255, 107, 107, 0.1)'
      : 'rgba(255, 165, 0, 0.1)'};
  color: ${({ status }) =>
    status === 'success' ? '#27ae60' : status === 'error' ? '#ff6b6b' : '#ffa500'};
`

const WrongChainWarning = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 8px 0 12px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  background: rgba(255, 165, 0, 0.1);
  color: #ffa500;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    flex-direction: column;
    align-items: flex-start;
  `};
`

const SwitchNetworkButton = styled.button`
  padding: 6px 12px;
  background: #ffa500;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    width: 100%;
  `};

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

// Chain display names
const CHAIN_DISPLAY_NAMES: Record<number, string> = {
  137: 'Polygon',
  56: 'BNB Chain',
  42161: 'Arbitrum'
}

function getChainDisplayName(chainId: number): string {
  return CHAIN_DISPLAY_NAMES[chainId] || `Chain ${chainId}`
}

// Chain configurations for wallet_addEthereumChain
const CHAIN_CONFIG: Record<
  number,
  {
    chainId: string
    chainName: string
    rpcUrls: string[]
    nativeCurrency: { name: string; symbol: string; decimals: number }
    blockExplorerUrls: string[]
  }
> = {
  137: {
    chainId: '0x89',
    chainName: 'Polygon',
    rpcUrls: ['https://polygon-rpc.com'],
    nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 },
    blockExplorerUrls: ['https://polygonscan.com']
  },
  56: {
    chainId: '0x38',
    chainName: 'BNB Smart Chain',
    rpcUrls: ['https://bsc-dataseed.binance.org'],
    nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
    blockExplorerUrls: ['https://bscscan.com']
  },
  42161: {
    chainId: '0xa4b1',
    chainName: 'Arbitrum One',
    rpcUrls: ['https://arb1.arbitrum.io/rpc'],
    nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
    blockExplorerUrls: ['https://arbiscan.io']
  }
}

async function switchNetwork(targetChainId: number): Promise<boolean> {
  const ethereum = (window as any).ethereum
  if (!ethereum) return false

  const config = CHAIN_CONFIG[targetChainId]
  if (!config) return false

  try {
    await ethereum.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: config.chainId }]
    })
    return true
  } catch (switchError) {
    // Chain not added to wallet, try to add it
    if ((switchError as any).code === 4902) {
      try {
        await ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [config]
        })
        return true
      } catch {
        return false
      }
    }
    return false
  }
}

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${({ theme }) => theme.text2};
`

const EmptyTitle = styled.div`
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.text1};
`

const EmptyText = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.text3};
`

const ConnectCard = styled.div`
  background: ${({ theme }) => theme.bg2};
  border-radius: 16px;
  padding: 48px 24px;
  margin-bottom: 16px;
  text-align: center;
`

const ConnectTitle = styled.h2`
  font-size: 22px;
  font-weight: 600;
  color: ${({ theme }) => theme.text1};
  margin-bottom: 12px;
`

const ConnectText = styled.p`
  font-size: 15px;
  color: ${({ theme }) => theme.text2};
  margin-bottom: 24px;
  line-height: 1.5;
`

const ConnectButton = styled.button`
  padding: 14px 32px;
  background: ${({ theme }) => theme.primary1};
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`

const StatsLink = styled(Link)`
  display: block;
  text-align: center;
  padding: 12px;
  color: ${({ theme }) => theme.text2};
  text-decoration: none;
  font-size: 14px;

  &:hover {
    color: ${({ theme }) => theme.primary1};
  }
`

// Two-step flow components
const StepContainer = styled.div`
  margin-bottom: 16px;
`

const StepHeader = styled.div<{ active: boolean; completed: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: ${({ theme, active, completed }) =>
    completed ? 'rgba(39, 174, 96, 0.1)' : active ? theme.bg1 : theme.bg3};
  border-radius: 12px;
  opacity: ${({ active, completed }) => (active || completed ? 1 : 0.5)};
  transition: all 0.2s;
`

const StepNumber = styled.div<{ active: boolean; completed: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  background: ${({ theme, active, completed }) =>
    completed ? '#27ae60' : active ? theme.primary1 : theme.bg2};
  color: ${({ active, completed }) => (active || completed ? 'white' : '#999')};
`

const StepContent = styled.div`
  flex: 1;
`

const StepTitle = styled.div<{ completed: boolean }>`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme, completed }) => (completed ? '#27ae60' : theme.text1)};
  margin-bottom: 2px;
`

const StepDescription = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.text3};
`

const StepDetails = styled.div<{ active: boolean; blocked?: boolean }>`
  padding: 16px;
  padding-top: 12px;
  opacity: ${({ blocked }) => (blocked ? 0.5 : 1)};
  pointer-events: ${({ blocked }) => (blocked ? 'none' : 'auto')};
`

const StepValue = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding: 12px;
  background: ${({ theme }) => theme.bg3};
  border-radius: 8px;
`

const StepValueLabel = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.text2};
`

const StepValueAmount = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.text1};
`

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M2.5 7L5.5 10L11.5 4"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

// Map chainId to chain name
function getChainName(chainId: number): string {
  switch (chainId) {
    case 137:
      return 'polygon'
    case 56:
      return 'bnb'
    case 42161:
      return 'arbitrum'
    default:
      return 'unknown'
  }
}

// Individual LP position card with withdraw functionality (follows same style as farm position)
function LPPositionCard({ position, pairs }: { position: LPHolder; pairs: PairData[] }) {
  const { chainId } = useActiveWeb3React()
  const { withdraw, reset, status, txHash, error, withdrawnTokens } = useSunsetWithdraw(
    position.pairAddress
  )
  const [switching, setSwitching] = useState(false)

  const isWrongChain = chainId !== position.chainId
  const isLoading = status === 'signing' || status === 'pending'
  const isComplete = status === 'success'

  // Format withdrawn amounts for display
  const withdrawnAmountsText = useMemo(() => {
    if (!withdrawnTokens || withdrawnTokens.length === 0) return ''
    return withdrawnTokens.map(t => `${parseFloat(t.amount).toFixed(4)} ${t.symbol}`).join(' + ')
  }, [withdrawnTokens])

  const handleSwitchNetwork = useCallback(async () => {
    setSwitching(true)
    await switchNetwork(position.chainId)
    setSwitching(false)
  }, [position.chainId])

  // Look up pair data to get token addresses
  const pairData = pairs.find(
    p => p.pairAddress.toLowerCase() === position.pairAddress.toLowerCase()
  )

  const handleWithdraw = useCallback(() => {
    if (isWrongChain) return
    withdraw()
  }, [isWrongChain, withdraw])

  const getButtonText = () => {
    switch (status) {
      case 'signing':
        return 'Signing...'
      case 'pending':
        return 'Removing Liquidity...'
      case 'success':
        return 'Liquidity Removed ✓'
      default:
        return 'Remove Liquidity'
    }
  }

  return (
    <PositionItem>
      {/* Header */}
      <PositionHeader>
        <PositionLeft>
          {pairData && (
            <PairTokenLogos
              token0Address={pairData.token0Address}
              token1Address={pairData.token1Address}
              size={24}
            />
          )}
          <PairName>{position.pairSymbol}</PairName>
          <ChainBadge chain={position.chain}>{position.chain}</ChainBadge>
        </PositionLeft>
        <PositionRight>
          <Value>{isComplete ? '$0.00' : formatUsd(position.balanceUsd)}</Value>
          <LPAmount>{isComplete ? 'Withdrawn' : 'Total withdrawable'}</LPAmount>
        </PositionRight>
      </PositionHeader>

      {isComplete ? (
        <StatusText status="success">
          {withdrawnAmountsText ? (
            <>Withdrawn: {withdrawnAmountsText}</>
          ) : (
            <>All done! Your tokens have been withdrawn.</>
          )}{' '}
          {txHash && (
            <a
              href={getExplorerUrl(position.chainId, txHash, 'tx')}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'inherit', textDecoration: 'underline' }}
            >
              View transaction
            </a>
          )}
        </StatusText>
      ) : (
        <StepContainer>
          <StepHeader active={!isComplete} completed={isComplete}>
            <StepNumber active={!isComplete} completed={isComplete}>
              {isComplete ? <CheckIcon /> : '1'}
            </StepNumber>
            <StepContent>
              <StepTitle completed={isComplete}>
                {isComplete ? 'Liquidity Removed' : 'Remove Liquidity'}
              </StepTitle>
              <StepDescription>
                {isComplete
                  ? 'Tokens received in your wallet'
                  : 'Convert LP tokens to underlying tokens'}
              </StepDescription>
            </StepContent>
          </StepHeader>

          <StepDetails active={true}>
            <StepValue>
              <StepValueLabel>LP to withdraw</StepValueLabel>
              <StepValueAmount>
                {position.balance.toFixed(6)} LP ({formatUsd(position.balanceUsd)})
              </StepValueAmount>
            </StepValue>

            {isWrongChain && (
              <WrongChainWarning>
                <span>Switch to {getChainDisplayName(position.chainId)} to withdraw</span>
                <SwitchNetworkButton onClick={handleSwitchNetwork} disabled={switching}>
                  {switching
                    ? 'Switching...'
                    : `Switch to ${getChainDisplayName(position.chainId)}`}
                </SwitchNetworkButton>
              </WrongChainWarning>
            )}

            {status === 'error' && error && (
              <StatusText status="error">
                {error}{' '}
                <span onClick={reset} style={{ cursor: 'pointer', textDecoration: 'underline' }}>
                  Try again
                </span>
              </StatusText>
            )}

            <WithdrawButton
              onClick={handleWithdraw}
              disabled={isWrongChain || isLoading || isComplete}
            >
              {getButtonText()}
            </WithdrawButton>
          </StepDetails>
        </StepContainer>
      )}
    </PositionItem>
  )
}

// Combined two-step withdrawal: Exit Farm → Remove All Liquidity
// This component handles the full withdrawal flow for users with farm positions
// After exiting farm, it withdraws ALL LP (including any direct LP holdings)
function GuidedWithdrawFlow({
  farmPosition,
  farmUsdValue,
  pairSymbol,
  pairData,
  directLpValue,
  directLpBalance,
  rewardTokenPrices,
  onComplete
}: {
  farmPosition: FarmPosition
  farmUsdValue: number
  pairSymbol: string
  pairData?: PairData
  directLpValue: number // USD value of any direct LP holdings on same pair
  directLpBalance: number // LP token balance of direct holdings
  rewardTokenPrices: { [address: string]: number }
  onComplete: () => void
}) {
  const { chainId } = useActiveWeb3React()

  // Step tracking: 1 = farm exit, 2 = LP removal
  const [currentStep, setCurrentStep] = useState<1 | 2>(1)
  const [step1Complete, setStep1Complete] = useState(false)
  const [switching, setSwitching] = useState(false)

  // Check if on wrong chain
  const isWrongChain = chainId !== farmPosition.chainId

  // Farm exit hook
  const {
    exit,
    reset: resetFarm,
    status: farmStatus,
    txHash: farmTxHash,
    error: farmError
  } = useFarmUnstake(farmPosition.farmAddress)

  // LP balance and withdrawal hook (uses the staking token which is the LP pair)
  const { balance: lpBalance, refresh: refreshLpBalance } = useLPBalance(farmPosition.stakingToken)
  const {
    withdraw: withdrawLp,
    reset: resetLp,
    status: lpStatus,
    txHash: lpTxHash,
    error: lpError
  } = useSunsetWithdraw(farmPosition.stakingToken)

  const chainName = getChainName(farmPosition.chainId)

  const handleSwitchNetwork = useCallback(async () => {
    setSwitching(true)
    await switchNetwork(farmPosition.chainId)
    setSwitching(false)
  }, [farmPosition.chainId])

  // Calculate reward USD values
  const rewardsWithUsd = useMemo(() => {
    return farmPosition.unclaimedRewards.map(reward => {
      const price = rewardTokenPrices[reward.tokenAddress.toLowerCase()] || 0
      const usdValue = reward.amountFormatted * price
      return { ...reward, usdValue, price }
    })
  }, [farmPosition.unclaimedRewards, rewardTokenPrices])

  const totalRewardsUsd = useMemo(() => {
    return rewardsWithUsd.reduce((sum, r) => sum + r.usdValue, 0)
  }, [rewardsWithUsd])

  // Calculate total value: staked LP + direct LP + rewards
  const totalLpValue = farmUsdValue + directLpValue
  const totalValue = totalLpValue + totalRewardsUsd
  const hasRewards = farmPosition.unclaimedRewards.length > 0

  // Auto-advance to step 2 when farm exit succeeds
  useEffect(() => {
    if (farmStatus === 'success' && !step1Complete) {
      setStep1Complete(true)
      setCurrentStep(2)
      // Refresh LP balance to get the newly received tokens
      setTimeout(() => refreshLpBalance(), 2000)
    }
  }, [farmStatus, step1Complete, refreshLpBalance])

  // Notify parent when complete
  useEffect(() => {
    if (lpStatus === 'success') {
      onComplete()
    }
  }, [lpStatus, onComplete])

  const handleExitFarm = useCallback(() => {
    exit()
  }, [exit])

  const handleWithdrawLp = useCallback(() => {
    withdrawLp()
  }, [withdrawLp])

  const getStep1ButtonText = () => {
    switch (farmStatus) {
      case 'pending':
        return 'Exiting Reward Pool...'
      case 'success':
        return 'Reward Pool Exited ✓'
      default:
        return 'Exit Reward Pool'
    }
  }

  const getStep2ButtonText = () => {
    switch (lpStatus) {
      case 'signing':
        return 'Sign Permit...'
      case 'pending':
        return 'Removing Liquidity...'
      case 'success':
        return 'Liquidity Removed ✓'
      default:
        return 'Remove All Liquidity'
    }
  }

  const step2Active = step1Complete && lpStatus !== 'success'
  const step2Completed = lpStatus === 'success'
  const allDone = step1Complete && step2Completed

  return (
    <PositionItem>
      {/* Header */}
      <PositionHeader>
        <PositionLeft>
          {pairData && (
            <PairTokenLogos
              token0Address={pairData.token0Address}
              token1Address={pairData.token1Address}
              size={24}
            />
          )}
          <PairName>{pairSymbol || 'LP Position'}</PairName>
          <ChainBadge chain={chainName}>{chainName}</ChainBadge>
        </PositionLeft>
        <PositionRight>
          <Value>{formatUsd(totalValue)}</Value>
          <LPAmount>Total withdrawable</LPAmount>
        </PositionRight>
      </PositionHeader>

      {allDone ? (
        <StatusText status="success">All done! Your tokens have been withdrawn.</StatusText>
      ) : (
        <>
          {/* Step 1: Exit Reward Pool */}
          <StepContainer>
            <StepHeader active={currentStep === 1 && !step1Complete} completed={step1Complete}>
              <StepNumber active={currentStep === 1} completed={step1Complete}>
                {step1Complete ? <CheckIcon /> : '1'}
              </StepNumber>
              <StepContent>
                <StepTitle completed={step1Complete}>
                  {step1Complete ? 'Reward Pool Exited' : 'Exit Reward Pool'}
                </StepTitle>
                <StepDescription>
                  {step1Complete
                    ? 'LP tokens and rewards received'
                    : 'Unstake LP tokens and claim any rewards'}
                </StepDescription>
              </StepContent>
            </StepHeader>

            {!step1Complete && (
              <StepDetails active={true}>
                <StepValue>
                  <StepValueLabel>Staked LP</StepValueLabel>
                  <StepValueAmount>
                    {formatStakedBalance(farmPosition.stakedBalance)} ({formatUsd(farmUsdValue)})
                  </StepValueAmount>
                </StepValue>
                {hasRewards &&
                  rewardsWithUsd.map((reward, i) => (
                    <StepValue key={i}>
                      <StepValueLabel>Unclaimed {reward.tokenSymbol}</StepValueLabel>
                      <StepValueAmount>
                        {reward.amountFormatted.toFixed(4)}
                        {reward.usdValue > 0 && ` (${formatUsd(reward.usdValue)})`}
                      </StepValueAmount>
                    </StepValue>
                  ))}

                {farmStatus === 'success' && farmTxHash && (
                  <StatusText status="success">
                    Success!{' '}
                    <a
                      href={getExplorerUrl(farmPosition.chainId, farmTxHash, 'tx')}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'inherit', textDecoration: 'underline' }}
                    >
                      View tx
                    </a>
                  </StatusText>
                )}

                {isWrongChain && (
                  <WrongChainWarning>
                    <span>Switch to {getChainDisplayName(farmPosition.chainId)} to continue</span>
                    <SwitchNetworkButton onClick={handleSwitchNetwork} disabled={switching}>
                      {switching
                        ? 'Switching...'
                        : `Switch to ${getChainDisplayName(farmPosition.chainId)}`}
                    </SwitchNetworkButton>
                  </WrongChainWarning>
                )}

                {farmStatus === 'error' && farmError && (
                  <StatusText status="error">
                    {farmError}{' '}
                    <span
                      onClick={resetFarm}
                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      Try again
                    </span>
                  </StatusText>
                )}

                <WithdrawButton
                  onClick={handleExitFarm}
                  disabled={isWrongChain || farmStatus === 'pending' || farmStatus === 'success'}
                >
                  {getStep1ButtonText()}
                </WithdrawButton>
              </StepDetails>
            )}
          </StepContainer>

          {/* Step 2: Remove All Liquidity */}
          <StepContainer>
            <StepHeader active={step2Active} completed={step2Completed}>
              <StepNumber active={step2Active} completed={step2Completed}>
                {step2Completed ? <CheckIcon /> : '2'}
              </StepNumber>
              <StepContent>
                <StepTitle completed={step2Completed}>
                  {step2Completed ? 'Liquidity Removed' : 'Remove All Liquidity'}
                </StepTitle>
                <StepDescription>
                  {step2Completed
                    ? 'Tokens received in your wallet'
                    : 'Convert all LP tokens to underlying tokens'}
                </StepDescription>
              </StepContent>
            </StepHeader>

            {!step2Completed && (
              <StepDetails active={step2Active} blocked={!step1Complete}>
                <StepValue>
                  <StepValueLabel>LP to withdraw</StepValueLabel>
                  <StepValueAmount>
                    {step1Complete
                      ? // After step 1: show updated wallet balance (includes direct + from farm)
                        lpBalance
                        ? `${(Number(lpBalance.toString()) / 1e18).toFixed(6)} LP (${formatUsd(
                            totalLpValue
                          )})`
                        : `${(
                            directLpBalance +
                            Number(formatStakedBalance(farmPosition.stakedBalance))
                          ).toFixed(6)} LP (${formatUsd(totalLpValue)})`
                      : // Before step 1: show direct LP balance only
                      directLpBalance > 0
                      ? `${directLpBalance.toFixed(6)} LP (${formatUsd(directLpValue)})`
                      : '0 LP (will receive from Step 1)'}
                  </StepValueAmount>
                </StepValue>

                {lpStatus === 'success' && lpTxHash && (
                  <StatusText status="success">
                    Success!{' '}
                    <a
                      href={getExplorerUrl(farmPosition.chainId, lpTxHash, 'tx')}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'inherit', textDecoration: 'underline' }}
                    >
                      View tx
                    </a>
                  </StatusText>
                )}

                {isWrongChain && step1Complete && (
                  <WrongChainWarning>
                    <span>Switch to {getChainDisplayName(farmPosition.chainId)} to continue</span>
                    <SwitchNetworkButton onClick={handleSwitchNetwork} disabled={switching}>
                      {switching
                        ? 'Switching...'
                        : `Switch to ${getChainDisplayName(farmPosition.chainId)}`}
                    </SwitchNetworkButton>
                  </WrongChainWarning>
                )}

                {lpStatus === 'error' && lpError && (
                  <StatusText status="error">
                    {lpError}{' '}
                    <span
                      onClick={resetLp}
                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      Try again
                    </span>
                  </StatusText>
                )}

                <WithdrawButton
                  onClick={handleWithdrawLp}
                  disabled={
                    !step1Complete ||
                    isWrongChain ||
                    lpStatus === 'signing' ||
                    lpStatus === 'pending' ||
                    lpStatus === 'success'
                  }
                >
                  {!step1Complete ? 'Complete Step 1 First' : getStep2ButtonText()}
                </WithdrawButton>
              </StepDetails>
            )}
          </StepContainer>
        </>
      )}
    </PositionItem>
  )
}

export default function Sunset() {
  const { account } = useActiveWeb3React()
  const toggleWalletModal = useWalletModalToggle()
  const { positions: userPositions, loading: positionsLoading } = useLivePositions()
  const { positions: farmPositions, loading: farmLoading, refresh: refreshFarm } = useFarmPosition()
  const { pairs, holders } = useSunsetData()

  // Track which farm positions have completed their guided flow
  const [completedFarmFlows, setCompletedFarmFlows] = useState<Set<string>>(new Set())

  // Calculate farm data for each farm position
  const farmPositionsWithData = useMemo(() => {
    return farmPositions
      .map(farmPosition => {
        const farmAddress = FARM_ADDRESSES[farmPosition.chainId]
        if (!farmAddress) return null

        // Find the farm contract as an LP holder to get price per LP
        const farmAsHolder = holders.find(
          h =>
            h.userAddress.toLowerCase() === farmAddress.toLowerCase() &&
            h.chainId === farmPosition.chainId
        )
        if (!farmAsHolder || farmAsHolder.balance <= 0) return null

        const pricePerLp = farmAsHolder.balanceUsd / farmAsHolder.balance
        const userStakedLp = Number(farmPosition.stakedBalance.toString()) / 1e18
        const farmUsdValue = userStakedLp * pricePerLp
        const farmPairAddress = farmAsHolder.pairAddress.toLowerCase()

        // Get pair data
        const pairData = pairs.find(
          p => p.pairAddress.toLowerCase() === farmAsHolder.pairAddress.toLowerCase()
        )
        const farmPairSymbol = farmAsHolder.pairSymbol || ''

        // Calculate direct LP value and balance on same pair
        const samePairLpPositions = userPositions.filter(
          p => p.pairAddress.toLowerCase() === farmPairAddress && p.chainId === farmPosition.chainId
        )
        const directLpValue = samePairLpPositions.reduce((sum, p) => sum + p.balanceUsd, 0)
        const directLpBalance = samePairLpPositions.reduce((sum, p) => sum + p.balance, 0)

        return {
          farmPosition,
          farmUsdValue,
          farmPairAddress,
          farmPairSymbol,
          farmPairData: pairData,
          directLpValue,
          directLpBalance
        }
      })
      .filter((data): data is NonNullable<typeof data> => data !== null)
  }, [farmPositions, holders, pairs, userPositions])

  // Get LP positions that are NOT on the same pair as any farm position
  const otherLpPositions = useMemo(() => {
    const farmPairKeys = new Set(
      farmPositionsWithData.map(f => `${f.farmPairAddress}-${f.farmPosition.chainId}`)
    )
    return userPositions.filter(
      p => !farmPairKeys.has(`${p.pairAddress.toLowerCase()}-${p.chainId}`)
    )
  }, [userPositions, farmPositionsWithData])

  // Collect reward token addresses for price fetching from all farm positions
  const rewardTokensByChain = useMemo(() => {
    const result: { [chainId: number]: string[] } = {}
    for (const { farmPosition } of farmPositionsWithData) {
      const tokens = farmPosition.unclaimedRewards.map(r => r.tokenAddress.toLowerCase())
      if (tokens.length > 0) {
        if (!result[farmPosition.chainId]) {
          result[farmPosition.chainId] = []
        }
        result[farmPosition.chainId].push(...tokens)
      }
    }
    return result
  }, [farmPositionsWithData])

  // Fetch reward token prices
  const { prices: rewardTokenPrices } = useTokenPrices(rewardTokensByChain)

  // Filter out completed farm flows
  const activeFarmPositions = useMemo(() => {
    return farmPositionsWithData.filter(
      f => !completedFarmFlows.has(`${f.farmPosition.farmAddress}-${f.farmPosition.chainId}`)
    )
  }, [farmPositionsWithData, completedFarmFlows])

  const hasPositions = userPositions.length > 0 || farmPositions.length > 0
  const hasFarmPositions = activeFarmPositions.length > 0

  // Handler when guided flow completes for a specific farm
  const handleGuidedFlowComplete = useCallback(
    (farmAddress: string, chainId: number) => {
      setCompletedFarmFlows(prev => new Set([...prev, `${farmAddress}-${chainId}`]))
      // Refresh farm positions to update state
      refreshFarm()
    },
    [refreshFarm]
  )

  // Disconnected state
  if (!account) {
    return (
      <PageWrapper>
        <Title>Withdraw Funds</Title>
        <Subtitle>The XATA protocol has been sunset.</Subtitle>

        <ConnectCard>
          <ConnectTitle>Connect Your Wallet</ConnectTitle>
          <ConnectText>
            Connect your wallet to check if you have any
            <br />
            liquidity positions or reward pool stakes to withdraw.
          </ConnectText>
          <ConnectButton onClick={toggleWalletModal}>Connect Wallet</ConnectButton>
        </ConnectCard>

        <StatsLink to="/stats">View more data →</StatsLink>
      </PageWrapper>
    )
  }

  // Connected state
  return (
    <PageWrapper>
      <Title>Withdraw Funds</Title>
      <Subtitle>
        The XATA protocol has been sunset.
        {hasPositions && (
          <>
            <br />
            {hasFarmPositions
              ? 'Follow the steps below to withdraw all your funds.'
              : 'You have positions to withdraw.'}
          </>
        )}
      </Subtitle>

      {/* Loading state */}
      {(farmLoading || positionsLoading) && (
        <Card>
          <PositionItem>
            <div style={{ textAlign: 'center', padding: '20px', opacity: 0.6 }}>
              Checking your positions...
            </div>
          </PositionItem>
        </Card>
      )}

      {/* Case 1: User has farm positions - show guided 2-step flow for each */}
      {activeFarmPositions.map(farmData => (
        <Card key={`farm-${farmData.farmPosition.farmAddress}-${farmData.farmPosition.chainId}`}>
          <GuidedWithdrawFlow
            farmPosition={farmData.farmPosition}
            farmUsdValue={farmData.farmUsdValue}
            pairSymbol={farmData.farmPairSymbol}
            pairData={farmData.farmPairData}
            directLpValue={farmData.directLpValue}
            directLpBalance={farmData.directLpBalance}
            rewardTokenPrices={rewardTokenPrices}
            onComplete={() =>
              handleGuidedFlowComplete(
                farmData.farmPosition.farmAddress,
                farmData.farmPosition.chainId
              )
            }
          />
        </Card>
      ))}

      {/* Case 2: Other LP positions (different pairs or after guided flow complete) */}
      {!farmLoading && !positionsLoading && otherLpPositions.length > 0 && (
        <Card>
          {otherLpPositions.map((position, i) => (
            <LPPositionCard
              key={`${position.pairAddress}-${position.chainId}-${i}`}
              position={position}
              pairs={pairs}
            />
          ))}
        </Card>
      )}

      {/* Case 3: No positions at all */}
      {!farmLoading && !positionsLoading && !hasFarmPositions && otherLpPositions.length === 0 && (
        <Card>
          <EmptyState>
            <EmptyTitle>No positions found</EmptyTitle>
            <EmptyText>
              {completedFarmFlows.size > 0
                ? 'All your positions have been withdrawn successfully!'
                : 'No liquidity positions or reward pool stakes found for this wallet.'}
            </EmptyText>
          </EmptyState>
        </Card>
      )}

      <StatsLink to="/stats">View more data →</StatsLink>
    </PageWrapper>
  )
}
